import { ChangeEvent } from 'react';
import styled from 'styled-components';
import { getColor } from '@gif/shared-global';

import { Input as InputComponent, InputProps } from '../Input/Input';
import { Themes } from '../../types';

export interface DateInputProps {
  legendIsRequired?: boolean;
  className?: string;
  monthLabel?: string;
  monthPlaceholder?: string;
  monthValue?: string;
  monthHasError?: boolean;
  monthHasValid?: boolean;
  monthErrorMessage?: string;
  defaultMonthValue?: string;
  dayLabel?: string;
  dayPlaceholder?: string;
  dayValue?: string;
  dayHasError?: boolean;
  dayHasValid?: boolean;
  dayErrorMessage?: string;
  defaultDayValue?: string;
  yearLabel?: string;
  yearPlaceholder?: string;
  yearValue?: string;
  yearHasError?: boolean;
  yearHasValid?: boolean;
  yearErrorMessage?: string;
  defaultYearValue?: string;
  onMonthChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onDayChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onYearChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  legend?: string;
  monthInputAttrs?: InputProps;
  dayInputAttrs?: InputProps;
  yearInputAttrs?: InputProps;
  themeId?: Themes;
}

const themeColors = {
  light: getColor('black'),
  dark: getColor('neutral100'),
};

const Wrapper = styled.fieldset`
  display: flex;
  padding: 0;
  border: none;
`;

const Legend = styled.legend<{
  $theme: Themes;
}>`
  margin-bottom: 1rem;
  color: ${({ $theme }) => themeColors[$theme]};
  font-size: 1rem;
  font-weight: 600;
`;

const DateInputContainer = styled.div`
  display: flex;
  gap: var(--space-space200, 1rem);
`;

const Input = styled(InputComponent)`
  input {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
`;

export function DateInput(props: DateInputProps) {
  const {
    legendIsRequired,
    className,
    defaultMonthValue,
    monthLabel = 'Month',
    monthPlaceholder = 'MM',
    monthValue,
    monthHasError,
    monthHasValid,
    monthErrorMessage,
    defaultDayValue,
    dayLabel = 'Day',
    dayPlaceholder = 'DD',
    dayValue,
    dayHasError,
    dayHasValid,
    dayErrorMessage,
    defaultYearValue,
    yearLabel = 'Year',
    yearPlaceholder = 'YYYY',
    yearValue,
    yearHasError,
    yearHasValid,
    yearErrorMessage,
    onMonthChange,
    onDayChange,
    onYearChange,
    legend,
    monthInputAttrs,
    dayInputAttrs,
    yearInputAttrs,
    themeId = Themes.LIGHT,
  } = props;
  return (
    <Wrapper className={className} aria-label={legend}>
      {legend && (
        <Legend $theme={themeId}>
          {legend}
          {legendIsRequired && <span aria-hidden>*</span>}
        </Legend>
      )}
      <DateInputContainer>
        <Input
          {...monthInputAttrs}
          label={monthLabel}
          name={'month'}
          placeholder={monthPlaceholder}
          {...(typeof defaultMonthValue !== 'undefined' &&
            typeof monthValue === 'undefined' && {
              defaultValue: defaultMonthValue,
            })}
          {...(typeof monthValue !== 'undefined' &&
            typeof defaultMonthValue === 'undefined' && { value: monthValue })}
          onChange={onMonthChange}
          hasError={monthHasError}
          hasValid={monthHasValid}
          themeId={themeId}
          errorMessage={monthErrorMessage}
        />
        <Input
          {...dayInputAttrs}
          label={dayLabel}
          name={'day'}
          placeholder={dayPlaceholder}
          {...(typeof defaultDayValue !== 'undefined' &&
            typeof dayValue === 'undefined' && {
              defaultValue: defaultDayValue,
            })}
          {...(typeof dayValue !== 'undefined' &&
            typeof defaultDayValue === 'undefined' && { value: dayValue })}
          onChange={onDayChange}
          hasError={dayHasError}
          hasValid={dayHasValid}
          themeId={themeId}
          errorMessage={dayErrorMessage}
        />
        <Input
          {...yearInputAttrs}
          label={yearLabel}
          name={'year'}
          placeholder={yearPlaceholder}
          {...(typeof defaultYearValue !== 'undefined' &&
            typeof yearValue === 'undefined' && {
              defaultValue: defaultYearValue,
            })}
          {...(typeof yearValue !== 'undefined' &&
            typeof defaultYearValue === 'undefined' && { value: yearValue })}
          onChange={onYearChange}
          hasError={yearHasError}
          hasValid={yearHasValid}
          themeId={themeId}
          errorMessage={yearErrorMessage}
        />
      </DateInputContainer>
    </Wrapper>
  );
}

export default DateInput;
